import React from 'react';

interface LogoProps {
  variant?: 'card' | 'inline' | 'footer' | 'iconOnly' | 'transparent' | 'header' | 'about';
  className?: string;
  imgClassName?: string;
}

export const Logo: React.FC<LogoProps> = ({ 
  variant = 'card', 
  className = '',
  imgClassName = ''
}) => {
  // Header variant: perfectly sized for mobile (320px+) through 4K displays
  if (variant === 'header') {
    return (
      <div className={`inline-flex items-center gap-2 sm:gap-3 ${className}`}>
        <div className="bg-white rounded-xl shadow-md p-1 sm:p-1.5 px-2 sm:px-2.5 border border-white/30 shrink-0 flex items-center justify-center">
          <img 
            src="/assets/ebon-logo.png" 
            alt="EBON Media Solutions Logo" 
            className={`h-7 sm:h-9 md:h-11 max-h-11 w-auto max-w-[80px] sm:max-w-[120px] object-contain block ${imgClassName}`}
          />
        </div>
        <div className="flex flex-col justify-center leading-none text-left select-none">
          <div className="flex items-center gap-1">
            <span className="text-sm sm:text-lg md:text-xl font-black text-white tracking-tight font-display drop-shadow-sm">
              EBON
            </span>
            <span className="text-sm sm:text-lg md:text-xl font-black text-[#159DE8] tracking-tight font-display drop-shadow-sm">
              Media
            </span>
          </div>
          <span className="text-[8px] sm:text-[10px] md:text-[11px] font-extrabold text-[#FF851B] tracking-[0.24em] uppercase mt-0.5">
            SOLUTIONS
          </span>
        </div>
      </div>
    );
  }

  // Footer variant: elegant, structured corporate lockup
  if (variant === 'footer') {
    return (
      <div className={`inline-flex flex-col items-start gap-2.5 ${className}`}>
        <div className="flex items-center gap-2.5">
          <div className="bg-white rounded-xl p-1.5 sm:p-2 px-2.5 sm:px-3 shadow-lg border border-white/20 shrink-0 flex items-center justify-center">
            <img 
              src="/assets/ebon-logo.png" 
              alt="EBON Media Solutions Logo" 
              className={`h-8 sm:h-10 md:h-12 max-h-12 w-auto max-w-[100px] sm:max-w-[140px] object-contain block ${imgClassName}`}
            />
          </div>
          <div className="flex flex-col leading-none text-left">
            <div className="flex items-center gap-1">
              <span className="text-base sm:text-xl font-black text-white tracking-tight font-display">
                EBON
              </span>
              <span className="text-base sm:text-xl font-black text-[#159DE8] tracking-tight font-display">
                Media
              </span>
            </div>
            <span className="text-[9px] sm:text-xs font-extrabold text-[#FF851B] tracking-[0.22em] uppercase mt-0.5">
              SOLUTIONS
            </span>
          </div>
        </div>
        <div className="text-[10px] sm:text-[11px] font-semibold text-[#8FA0B8] tracking-wider uppercase pl-0.5">
          Innovate • Automate • Elevate
        </div>
      </div>
    );
  }

  // About variant: showcase badge for About Us hero and leadership sections
  if (variant === 'about') {
    return (
      <div className={`inline-flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl bg-[#0c1a2d]/90 border border-[#159DE8]/30 backdrop-blur-md shadow-xl ${className}`}>
        <div className="bg-white rounded-xl p-2 sm:p-2.5 px-3 sm:px-4 shadow-xl border border-white/40 shrink-0 flex items-center justify-center">
          <img 
            src="/assets/ebon-logo.png" 
            alt="EBON Media Solutions Master Logo" 
            className={`h-10 sm:h-14 md:h-16 max-h-16 w-auto max-w-[120px] sm:max-w-none object-contain block ${imgClassName}`}
          />
        </div>
        <div className="flex flex-col text-left space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="text-xl sm:text-2xl font-black text-white font-display tracking-tight">
              EBON <span className="text-[#159DE8]">Media</span>
            </span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#159DE8]/10 text-[#159DE8] border border-[#159DE8]/30 uppercase tracking-widest font-bold">
              Official
            </span>
          </div>
          <span className="text-[10px] sm:text-xs font-extrabold text-[#FF851B] tracking-[0.25em] uppercase">
            SOLUTIONS
          </span>
          <p className="text-[11px] text-[#8FA0B8] font-medium pt-0.5">
            Innovative Technology • Intelligent Automation • Sustainable Growth
          </p>
        </div>
      </div>
    );
  }

  // Card variant
  if (variant === 'card') {
    return (
      <div className={`inline-flex items-center gap-2.5 ${className}`}>
        <div className="bg-white rounded-xl shadow-md p-1 sm:p-1.5 px-2 sm:px-2.5 border border-white/30 shrink-0 flex items-center justify-center">
          <img 
            src="/assets/ebon-logo.png" 
            alt="EBON Media Solutions Logo" 
            className={`h-7 sm:h-9 max-h-9 w-auto max-w-[90px] sm:max-w-[120px] object-contain block ${imgClassName}`}
          />
        </div>
        <div className="flex flex-col leading-none text-left">
          <span className="text-sm sm:text-base font-black text-white tracking-tight">
            EBON <span className="text-[#159DE8]">Media</span>
          </span>
          <span className="text-[9px] sm:text-[10px] font-bold text-[#FF851B] tracking-widest uppercase mt-0.5">
            SOLUTIONS
          </span>
        </div>
      </div>
    );
  }

  // IconOnly variant
  if (variant === 'iconOnly') {
    return (
      <div className={`inline-flex items-center justify-center bg-white rounded-xl p-1 shadow-md shrink-0 ${className}`}>
        <img 
          src="/assets/ebon-logo-icon.png" 
          alt="EBON Media Icon" 
          className={`h-7 sm:h-9 max-h-9 w-auto object-contain block ${imgClassName}`}
        />
      </div>
    );
  }

  // Transparent variant
  if (variant === 'transparent') {
    return (
      <div className={`inline-flex items-center gap-2.5 ${className}`}>
        <img 
          src="/assets/ebon-logo-transparent.png" 
          alt="EBON Media Solutions Logo" 
          className={`h-9 sm:h-12 max-h-12 w-auto max-w-[110px] sm:max-w-none object-contain block ${imgClassName}`}
        />
        <div className="flex flex-col leading-none text-left">
          <span className="text-base sm:text-lg font-black text-white tracking-tight">
            EBON <span className="text-[#159DE8]">Media</span>
          </span>
          <span className="text-[9px] sm:text-xs font-bold text-[#FF851B] tracking-widest uppercase mt-0.5">
            SOLUTIONS
          </span>
        </div>
      </div>
    );
  }

  // Default inline
  return (
    <div className={`inline-flex items-center gap-2.5 bg-white rounded-xl p-1.5 px-3 shadow-md border border-white/20 shrink-0 ${className}`}>
      <img 
        src="/assets/ebon-logo.png" 
        alt="EBON Media Solutions Logo" 
        className={`h-7 sm:h-9 max-h-9 w-auto max-w-[100px] object-contain block ${imgClassName}`}
      />
      <div className="flex flex-col leading-none text-left">
        <span className="text-sm font-black text-[#071426] tracking-tight">
          EBON Media
        </span>
        <span className="text-[9px] font-bold text-[#FF851B] tracking-widest uppercase mt-0.5">
          SOLUTIONS
        </span>
      </div>
    </div>
  );
};
